/**
 * Command palette registry — commands EXECUTE, not just navigate.
 *
 * Kept outside GlobalCommandBar so the component stays presentational and
 * under the size cap. Wiring targets real platform capabilities only:
 * - "Create abandoned-cart campaign" builds the actual draft flow from the
 *   recommended template graph (templateActions.activateTemplate) and lands
 *   in its canvas. Heavy template modules load on demand (dynamic import)
 *   so the always-mounted palette adds nothing to the main bundle.
 * - "Create customer segment" creates (or reuses) a real Segment via
 *   POST /api/segments and opens Business Hub scoped to it (?audience=).
 * - "Export customers" performs the authenticated CSV download in place.
 * - Remaining rows deep-link to canonical screens (no legacy-redirect hops);
 *   subject-line work lands in the live AI automations chat.
 */

import type React from 'react';
import type { NavigateFunction } from 'react-router-dom';
import {
  Workflow,
  Mail,
  Share2,
  Users,
  ShoppingCart,
  BarChart3,
  Settings,
  FileText,
  Keyboard,
} from 'lucide-react';

import { toast } from '../../stores/toastStore';
import { SHOW_SHORTCUTS_EVENT } from '../KeyboardShortcutsSheet';
import { segmentsApi, type SegmentDefinition } from '../../services/api/customerApi';
import { downloadCustomersCsv } from '../../services/api/analyticsExportApi';

export type CommandCategory = 'AI' | 'Navigation' | 'Action';

export interface CommandSuggestion {
  id: string;
  icon: React.ElementType;
  label: string;
  category: CommandCategory;
  action: () => void | Promise<void>;
  keywords?: string[];
}

export const COMMAND_GROUPS: { key: CommandCategory; title: string }[] = [
  { key: 'AI', title: 'Quick actions' },
  { key: 'Navigation', title: 'Navigation' },
  { key: 'Action', title: 'Actions' },
];

/** The hub template that carries the prebuilt abandoned-cart email graph. */
const CART_TEMPLATE_TRIGGER_ID = 'cart_abandon_1hr';

const HIGH_VALUE_SEGMENT: {
  name: string;
  description: string;
  definition: SegmentDefinition;
} = {
  name: 'High-value repeat buyers',
  description: 'Customers with 2+ orders and at least $1,000 lifetime spend',
  definition: {
    combinator: 'AND',
    rules: [
      { field: 'totalOrders', operator: 'greater_than', value: 1 },
      { field: 'totalSpent', operator: 'greater_or_equal', value: 1000 },
    ],
  },
};

/** Create the draft abandoned-cart flow from the recommended template graph. */
async function createAbandonedCartCampaign(navigate: NavigateFunction): Promise<void> {
  toast.info('Creating your abandoned-cart campaign…');
  try {
    const [{ activateTemplate }, { HUB_TEMPLATES }] = await Promise.all([
      import('../../pages/email-marketing/templateActions'),
      import('../../pages/email-marketing/hubTemplates'),
    ]);
    const template = HUB_TEMPLATES.find((t) => t.emailTriggerId === CART_TEMPLATE_TRIGGER_ID);
    if (!template) {
      navigate('/studio');
      return;
    }
    // activateTemplate toasts success and navigates into /studio/flow/:id.
    await activateTemplate(template, navigate);
  } catch {
    toast.error('Could not create the campaign — opening Automations instead');
    navigate('/studio');
  }
}

/** Create (or reuse) the high-value segment, then open the Hub scoped to it. */
async function createHighValueSegment(navigate: NavigateFunction): Promise<void> {
  try {
    const existing = (await segmentsApi.list()).data?.find(
      (s) => s.name === HIGH_VALUE_SEGMENT.name,
    );
    const segment = existing ?? (await segmentsApi.create(HIGH_VALUE_SEGMENT)).data;
    toast.success(
      existing
        ? `Opening "${HIGH_VALUE_SEGMENT.name}"`
        : `Segment created: ${HIGH_VALUE_SEGMENT.name}`,
    );
    navigate(segment?.id ? `/business?audience=${segment.id}` : '/business/customers');
  } catch {
    toast.error('Could not create the segment');
    navigate('/business/customers');
  }
}

/** Download the full customer CSV right from the palette. */
async function exportCustomersCsv(): Promise<void> {
  toast.info('Exporting customers to CSV…');
  try {
    await downloadCustomersCsv();
    toast.success('Customer CSV downloaded');
  } catch {
    toast.error('Failed to export customers');
  }
}

export function buildCommandSuggestions(navigate: NavigateFunction): CommandSuggestion[] {
  return [
    {
      id: 'create-campaign',
      icon: Workflow,
      label: 'Create email campaign for abandoned carts',
      category: 'AI',
      action: () => createAbandonedCartCampaign(navigate),
      keywords: ['email', 'campaign', 'abandoned', 'cart', 'flow', 'recover'],
    },
    {
      id: 'analyze-revenue',
      icon: BarChart3,
      label: 'Analyze revenue trends (last 30 days)',
      category: 'AI',
      action: () => navigate('/business?tab=overview'),
      keywords: ['analyze', 'revenue', 'trends', 'analytics'],
    },
    {
      id: 'segment-customers',
      icon: Users,
      label: 'Create customer segment: High-value repeat buyers',
      category: 'AI',
      action: () => createHighValueSegment(navigate),
      keywords: ['segment', 'customer', 'high-value', 'repeat', 'vip'],
    },
    {
      id: 'optimize-subject',
      icon: Mail,
      label: 'Optimize email subject lines with AI',
      category: 'AI',
      action: () => navigate('/studio?tab=chat'),
      keywords: ['email', 'subject', 'optimize', 'ai', 'assistant', 'chat'],
    },
    {
      id: 'social-post',
      icon: Share2,
      label: 'Compose a social media post',
      category: 'AI',
      action: () => navigate('/marketing/social?tab=composer'),
      keywords: ['social', 'post', 'compose', 'generate', 'publish'],
    },
    {
      id: 'nav-campaigns',
      icon: Mail,
      label: 'Go to Email Marketing',
      category: 'Navigation',
      action: () => navigate('/studio'),
      keywords: ['email', 'marketing', 'campaigns', 'studio', 'automations'],
    },
    {
      id: 'nav-customers',
      icon: Users,
      label: 'Go to Business Hub',
      category: 'Navigation',
      action: () => navigate('/business'),
      keywords: ['customers', 'business', 'hub'],
    },
    {
      id: 'nav-settings',
      icon: Settings,
      label: 'Go to Settings',
      category: 'Navigation',
      action: () => navigate('/account'),
      keywords: ['settings', 'preferences', 'account'],
    },
    {
      id: 'export-customers',
      icon: FileText,
      label: 'Export customer list to CSV',
      category: 'Action',
      action: () => exportCustomersCsv(),
      keywords: ['export', 'customer', 'csv', 'download'],
    },
    {
      id: 'view-orders',
      icon: ShoppingCart,
      label: 'View recent orders',
      category: 'Action',
      action: () => navigate('/business/orders'),
      keywords: ['orders', 'recent', 'purchases'],
    },
    {
      id: 'show-shortcuts',
      icon: Keyboard,
      label: 'Show keyboard shortcuts',
      category: 'Action',
      // AppLayout owns the sheet; the event avoids prop-drilling it here.
      action: () => window.dispatchEvent(new Event(SHOW_SHORTCUTS_EVENT)),
      keywords: ['keyboard', 'shortcuts', 'keys', 'help', 'hotkeys'],
    },
  ];
}
